import React from 'react';
import { 
  Box,
  Typography,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '../icons/StarIcon';

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '32px',
  position: 'relative'
}));

const ComingSoonOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  zIndex: 10
}));

const ComingSoonText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 700,
  color: '#1E3A8A',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  textAlign: 'center'
}));

const ComingSoonSubtext = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
  marginTop: '8px',
  textAlign: 'center'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '16px'
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '4px',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  overflowX: 'auto',
  [theme.breakpoints.down('md')]: {
    '& table': {
      minWidth: '650px'
    }
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#1E3A8A'
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#FFFFFF',
  padding: '16px',
  borderBottom: 'none'
}));

const DataCell = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#121927',
  padding: '16px',
  borderBottom: '1px solid #F0F0F0'
}));

const RatingContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '3px'
}));

const StatusText = styled(Typography)(({ theme, status }) => {
  let color = '#121927';
  if (status === 'Active') color = '#34A853';
  else if (status === 'Weak') color = '#EF232A';
  else if (status === 'Moderate') color = '#0B91D6';
  
  return {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    color
  };
});

const CompetitorSummaryTable = ({ competitorSummary }) => {
  const columns = [
    'Metric', 
    'Your Business', 
    'Compared to Competitor A', 
    'Compared to Competitor A', 
    'Compared to Competitor C'
  ];

  const renderCellContent = (value, metric) => {
    // Handle GBP Rating with stars
    if (metric === 'GBP Rating' && typeof value === 'number') {
      return (
        <RatingContainer>
          <StarIcon width={16} height={16} color="#FBBC05" />
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#121927' }}>
            {value.toFixed(1)}
          </Typography>
        </RatingContainer>
      );
    }
    
    // Handle Social Media Presence with colored text
    if (metric === 'Social Media Presence') {
      return <StatusText status={value}>{value}</StatusText>;
    }
    
    // Default rendering
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#121927' }}>
        {value}
      </Typography>
    );
  };

  return (
    <SectionContainer>
      <SectionTitle>Competitor Summary Table</SectionTitle>
      
      <ComingSoonOverlay>
        <Box>
          <ComingSoonText>Coming Soon</ComingSoonText>
          <ComingSoonSubtext>Competitor Summary Table</ComingSoonSubtext>
        </Box>
      </ComingSoonOverlay>
      
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              {columns.map((column, index) => (
                <HeaderCell key={index}>{column}</HeaderCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {competitorSummary.map((item, index) => (
              <TableRow key={index}>
                <DataCell>
                  <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#121927' }}>
                    {item.metric}
                  </Typography>
                </DataCell>
                <DataCell>
                  {renderCellContent(item.yourBusiness, item.metric)}
                </DataCell>
                <DataCell>
                  {renderCellContent(item.competitorA, item.metric)}
                </DataCell>
                <DataCell>
                  {renderCellContent(item.competitorB, item.metric)}
                </DataCell>
                <DataCell>
                  {renderCellContent(item.competitorC, item.metric)}
                </DataCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </SectionContainer>
  );
};

export default CompetitorSummaryTable;